import { useState } from "react";

interface SlidesViewerProps {
  slides: any[];
  language: string;
  t: (key: string) => string;
  onTakeQuiz?: () => void;
  showTakeQuizButton?: boolean;
}

const SlidesViewer = ({ slides, language, t, onTakeQuiz, showTakeQuizButton }: SlidesViewerProps) => {
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);

  if (!slides || slides.length === 0) return null;

  const currentSlide = slides[currentSlideIdx];
  const slideTitle = currentSlide?.title?.[language] || `Slide ${currentSlideIdx + 1}`;

  // Function to format content with proper line breaks for better readability
  const formatSlideContent = (content: string) => {
    if (!content) return "";

    // First, ensure consistent line breaks for processing
    let formattedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    return formattedContent
      // Add line breaks and indent after colons followed by a space
      .replace(/:\s+/g, ':<br/><div style="margin-left: 1.5rem;">')
      // Add line breaks and indent for numbered lists (1., 2., 3., etc.)
      // This regex adds line breaks before numbers and wraps the entire line in indented div
      .replace(/^(\d+\.\s*.*)/gm, '<br/><div style="margin-left: 1.5rem;">$1</div>')
      // Add line breaks and indent for bullet points or dashes
      // This regex adds line breaks before bullets and wraps the entire line in indented div
      .replace(/^(\s*[-•]\s*.*)/gm, '<br/><div style="margin-left: 1.5rem;">$1</div>')
      // Add line breaks before bold text patterns (only after colons) with indentation
      .replace(/(:)\s+([A-Z][a-z]+:)/g, '$1<br/><div style="margin-left: 1.5rem;"><strong>$2</strong>')
      // Close any unclosed divs at the end of sentences
      .replace(/([.!?])\s+/g, '$1</div> ')
      // Clean up any trailing unclosed divs
      .replace(/([^>])(<br\/>)$/, '$1</div>$2')
      // Clean up multiple consecutive line breaks
      .replace(/(<br\/>){3,}/g, '<br/>');
  };

  const goToNextSlide = () => {
    if (currentSlideIdx < slides.length - 1) {
      setCurrentSlideIdx(currentSlideIdx + 1);
    }
  };

  const goToPreviousSlide = () => {
    if (currentSlideIdx > 0) {
      setCurrentSlideIdx(currentSlideIdx - 1);
    }
  };

  return (
    <div className="mb-6 sm:mb-8">
      <div className="text-center mb-4 sm:mb-6">
        <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full text-xs sm:text-sm font-medium mb-2 sm:mb-3">
          <svg
            className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          {t("courses.slides")}
        </div>
        <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-400 dark:to-primary-600 bg-clip-text text-transparent px-2">
          {t("courses.learningMaterials")}
        </h3>
      </div>
      <div className="flex justify-center px-2 sm:px-0">
        <div className="w-full max-w-4xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200/50 dark:border-gray-700/50 rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl p-3 sm:p-6 lg:p-8 backdrop-blur-sm">
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
              <h4 className="text-sm sm:text-lg lg:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent break-words flex-1">
                {slideTitle}
              </h4>
              <div className="flex items-center px-2 sm:px-3 py-1 bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-full flex-shrink-0">
                <span className="text-xs sm:text-sm font-semibold text-primary-700 dark:text-primary-300 whitespace-nowrap">
                  {currentSlideIdx + 1} / {slides.length}
                </span>
              </div>
            </div>
            <div className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentSlideIdx + 1) / slides.length) * 100}%`,
                }}
              ></div>
            </div>
          </div>
          {currentSlide?.imageUrl && (
            <div className="mb-4 sm:mb-6">
              <img
                src={currentSlide.imageUrl}
                alt={slideTitle}
                className="w-full h-40 sm:h-48 lg:h-64 object-cover rounded-lg shadow-lg"
              />
            </div>
          )}
          {currentSlide?.content && (
            <div className="mb-4 sm:mb-6">
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-6 lg:p-8 border border-gray-200 dark:border-gray-600">
                <div
                  className="prose prose-xs sm:prose-sm lg:prose-lg prose-gray dark:prose-invert max-w-none leading-normal text-gray-800 dark:text-gray-200 break-words overflow-wrap-anywhere"
                  style={{
                    fontSize: 'clamp(0.75rem, 2.5vw, 1.1rem)',
                    lineHeight: '1.5',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatSlideContent(currentSlide.content[language] || currentSlide.content.en || ""),
                  }}
                  />
                </div>
              </div>
          )}
          <div className="flex justify-between items-center gap-2 sm:gap-4">
            <button
              onClick={goToPreviousSlide}
              disabled={currentSlideIdx === 0}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                currentSlideIdx === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
              }`}
            >
              {t("common.previous")}
            </button>
            <button
              onClick={goToNextSlide}
              disabled={currentSlideIdx === slides.length - 1}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                currentSlideIdx === slides.length - 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              }`}
            >
              {t("common.next")}
            </button>
          </div>
        </div>
      </div>

      {/* Take Quiz Button - Show only on last slide */}
      {showTakeQuizButton && 
       currentSlideIdx === slides.length - 1 && 
       onTakeQuiz && (
        <div className="mt-4 sm:mt-6 text-center px-2">
          <button
            className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-lg shadow-lg hover:from-primary-600 hover:to-primary-800 transition-all duration-200 text-base sm:text-lg font-semibold tracking-wide"
            onClick={onTakeQuiz}
          >
            <span className="inline-block animate-bounce mr-2">🎯</span>
            {t("courses.takeQuiz")}
          </button>
        </div>
      )}
    </div>
  );
};

export default SlidesViewer;
